import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './hooks/useAuth'
import { AutonomousAgentProvider } from './contexts/AutonomousAgentContext'
import LoginPage        from './pages/LoginPage'
import RegisterPage     from './pages/RegisterPage'
import DashboardPage    from './pages/DashboardPage'
import ConsultancyPage  from './pages/ConsultancyPage'
import BusinessUnitPage from './pages/BusinessUnitPage'
import PhasePage        from './pages/PhasePage'
import SectionPage      from './pages/SectionPage'
import AdminPage        from './pages/AdminPage'
import ClientPortalPage from './pages/ClientPortalPage'
import CalendarPage          from './pages/CalendarPage'
import AnalyticsPage         from './pages/AnalyticsPage'
import AgenteEstrategicoPage from './pages/AgenteEstrategico'
import ReunionPage      from './pages/ReunionPage'
import ConsultancyDashboardPage from './pages/ConsultancyDashboardPage'
import CeoDashboardPage from './pages/CeoDashboardPage'
import CajaDashboardPage from './pages/CajaDashboardPage'
import PricingPage      from './pages/PricingPage'
import LandingPage      from './pages/LandingPage'
import LegalPage        from './pages/LegalPage'
import Layout           from './components/layout/Layout'
import SmartLayout      from './components/layout/SmartLayout'

// Renderiza CeoDashboardPage para CEOs Cliente y ConsultancyDashboardPage
// para staff NVC. Comparte URL /consultancy/:id/dashboard.
function DashboardSwitcher() {
  const { isClientCeo } = useAuth()
  return isClientCeo ? <CeoDashboardPage /> : <ConsultancyDashboardPage />
}

// "/" — visitante sin sesión ve LandingPage; con sesión, redirige a la home
// correspondiente al rol (NVC dashboard / caja / portal / CEO).
function LandingOrDashboard() {
  const { user, profile, loading } = useAuth()
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-brand-800">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-cream-100 border-t-transparent" />
    </div>
  )
  if (!user) return <LandingPage />
  // Authenticated — route by role
  if (profile?.role === 'caja_user')   return <Navigate to="/caja"   replace />
  if (profile?.role === 'client')      return <Navigate to="/portal" replace />
  if (profile?.role === 'client_ceo') {
    return profile.consultancy_id
      ? <Navigate to={`/consultancy/${profile.consultancy_id}/dashboard`} replace />
      : <Navigate to="/login" replace />
  }
  // admin / consultant
  return <Layout><DashboardPage /></Layout>
}

function ProtectedRoute({ children, requireNVC = false, requireAdmin = false, requireCaja = false, allowCeo = false }) {
  const { user, profile, loading } = useAuth()
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-brand-700">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-white border-t-transparent" />
    </div>
  )
  if (!user) return <Navigate to="/login" replace />
  // Helper: where to send a CEO who hits a page they can't access
  const ceoHome = profile?.consultancy_id ? `/consultancy/${profile.consultancy_id}/dashboard` : '/login'
  if (requireAdmin && profile?.role !== 'admin') return <Navigate to="/" replace />
  if (requireNVC && !['admin','consultant'].includes(profile?.role)) {
    // CEO trying to hit NVC-only views (admin/calendar/analytics/agente/reunion/etc)
    if (profile?.role === 'client_ceo') {
      // The route is consultancy-scoped (allowCeo=true) → fall through to
      // render. But here allowCeo is false, so we redirect to the CEO's
      // own dashboard.
      if (!allowCeo) return <Navigate to={ceoHome} replace />
    } else {
      // caja users trying to access NVC routes → send to caja dashboard
      if (profile?.role === 'caja_user') return <Navigate to="/caja" replace />
      return <Navigate to="/portal" replace />
    }
  }
  if (requireCaja && profile?.role !== 'caja_user') return <Navigate to="/" replace />
  // CEO can ONLY hit consultancy-scoped routes that explicitly opt-in via
  // allowCeo. The "/" landing is handled by the catch-all below.
  return children
}

function AppRoutes() {
  const { profile, loading, isCaja } = useAuth()
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-brand-700">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-white border-t-transparent" />
    </div>
  )
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />

      {/* Public marketing pages — no auth required */}
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/terms"   element={<LegalPage kind="terms" />} />
      <Route path="/privacy" element={<LegalPage kind="privacy" />} />
      <Route path="/refunds" element={<LegalPage kind="refunds" />} />

      {/* Landing público — entrada visitantes no autenticados.
          Para visitantes autenticados, redirige a su dashboard correspondiente. */}
      <Route path="/" element={<LandingOrDashboard />} />
      <Route path="/consultancy/:id" element={
        <ProtectedRoute requireNVC allowCeo>
          <Layout><ConsultancyPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/consultancy/:id/dashboard" element={
        <ProtectedRoute requireNVC allowCeo>
          <Layout><DashboardSwitcher /></Layout>
        </ProtectedRoute>
      } />
      {/* Project-level phase routes (no BU)
          Compartidas entre NVC staff, CEO Cliente y caja_user. SmartLayout
          escoge el shell correcto según rol. RLS controla qué datos puede
          leer cada uno. NO usar requireNVC aquí — bloquearía caja_user. */}
      <Route path="/consultancy/:id/phase/:phaseId" element={
        <ProtectedRoute>
          <SmartLayout><PhasePage /></SmartLayout>
        </ProtectedRoute>
      } />
      <Route path="/consultancy/:id/phase/:phaseId/section/:sectionId" element={
        <ProtectedRoute>
          <SmartLayout><SectionPage /></SmartLayout>
        </ProtectedRoute>
      } />

      {/* BU-level phase routes (mismo criterio que arriba) */}
      <Route path="/consultancy/:id/bu/:buId" element={
        <ProtectedRoute>
          <SmartLayout><BusinessUnitPage /></SmartLayout>
        </ProtectedRoute>
      } />
      <Route path="/consultancy/:id/bu/:buId/phase/:phaseId" element={
        <ProtectedRoute>
          <SmartLayout><PhasePage /></SmartLayout>
        </ProtectedRoute>
      } />
      <Route path="/consultancy/:id/bu/:buId/phase/:phaseId/section/:sectionId" element={
        <ProtectedRoute>
          <SmartLayout><SectionPage /></SmartLayout>
        </ProtectedRoute>
      } />
      <Route path="/calendar" element={
        <ProtectedRoute requireNVC>
          <Layout><CalendarPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/analytics" element={
        <ProtectedRoute requireNVC>
          <Layout><AnalyticsPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/agente" element={
        <ProtectedRoute requireNVC>
          <AgenteEstrategicoPage />
        </ProtectedRoute>
      } />
      <Route path="/reunion" element={
        <ProtectedRoute requireNVC>
          <Layout><ReunionPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute requireAdmin>
          <Layout><AdminPage /></Layout>
        </ProtectedRoute>
      } />

      {/* Client portal */}
      <Route path="/portal" element={
        <ProtectedRoute>
          <ClientPortalPage />
        </ProtectedRoute>
      } />
      <Route path="/portal/:id" element={
        <ProtectedRoute>
          <ClientPortalPage />
        </ProtectedRoute>
      } />

      {/* ── Caja routes — self-service consulting product ── */}
      <Route path="/caja" element={
        <ProtectedRoute requireCaja>
          <CajaDashboardPage />
        </ProtectedRoute>
      } />
      {/* Demo navegable — cualquier usuario autenticado puede explorarla */}
      <Route path="/caja/demo" element={
        <ProtectedRoute>
          <CajaDashboardPage forceDemo />
        </ProtectedRoute>
      } />
      {/* Las rutas de fase/sección compartidas ya están arriba (sin
          requireNVC, con SmartLayout) — accesibles para staff, CEO y caja. */}

      <Route path="*" element={
        profile?.role === 'client'     ? <Navigate to="/portal" replace /> :
        profile?.role === 'caja_user'  ? <Navigate to="/caja"   replace /> :
        profile?.role === 'client_ceo' ? (
          profile?.consultancy_id
            ? <Navigate to={`/consultancy/${profile.consultancy_id}/dashboard`} replace />
            : <Navigate to="/login" replace />
        ) :
                                         <Navigate to="/"        replace />
      } />
    </Routes>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AutonomousAgentProvider>
          <AppRoutes />
        </AutonomousAgentProvider>
      </AuthProvider>
    </BrowserRouter>
  )
}
